import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useCardsStore } from '@/stores'
import { formatLidForDisplay } from '@/utils/lid'
import './IndexCard.css'

interface ReferencePickerProps {
  existingReferences: string[]
  onSelect: (lid: string) => void
  onClose: () => void
}

export function ReferencePicker({ existingReferences, onSelect, onClose }: ReferencePickerProps) {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const { cards } = useCardsStore()

  const filteredCards = useMemo(() => {
    const allCards = Array.from(cards.values())
    const lowerQuery = searchQuery.toLowerCase()

    return allCards
      .filter((card) => {
        // Exclude inbox cards
        if (card.topicId === 'inbox') return false
        // Filter by search query
        if (searchQuery) {
          return (
            card.title.toLowerCase().includes(lowerQuery) ||
            card.lid.toLowerCase().includes(lowerQuery)
          )
        }
        return true
      })
      .slice(0, 50) // Limit results
  }, [cards, searchQuery])

  const handleSelect = (lid: string) => {
    if (!existingReferences.includes(lid)) {
      onSelect(lid)
    }
  }

  return (
    <div className="reference-picker-overlay" onClick={onClose}>
      <div className="reference-picker-modal" onClick={(e) => e.stopPropagation()}>
        <div className="reference-picker-header">
          <h3>{t('index.selectCard')}</h3>
        </div>

        <div className="reference-picker-search">
          <input
            type="text"
            placeholder={t('search.placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
        </div>

        <div className="reference-picker-content">
          {filteredCards.length === 0 ? (
            <p className="index-references-empty">{t('common.noResults')}</p>
          ) : (
            <div className="reference-picker-list">
              {filteredCards.map((card) => {
                const isAlreadyAdded = existingReferences.includes(card.lid)
                return (
                  <div
                    key={card.lid}
                    className={`reference-picker-item ${isAlreadyAdded ? 'disabled' : ''}`}
                    onClick={() => !isAlreadyAdded && handleSelect(card.lid)}
                  >
                    <span className="reference-lid-badge">{formatLidForDisplay(card.lid)}</span>
                    <span className="reference-title">{card.title}</span>
                    {isAlreadyAdded && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                        Added
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div className="reference-picker-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  )
}
